import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import type { ApiKeyDataScope } from '../types/apiKeyDataScope';

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'super_admin' | 'user' | 'moderator';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  emailVerified: boolean;
  photoUrl?: string;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  suspendedUsers: number;
  pendingUsers: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  adminUsers: number;
  regularUsers: number;
  moderatorUsers: number;
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface CreateUserRequest {
  email: string;
  password?: string;
  displayName?: string;
  role?: 'admin' | 'super_admin' | 'user' | 'moderator';
  sendInvitationEmail?: boolean;
}

export interface UpdateUserRequest {
  displayName?: string;
  role?: 'admin' | 'super_admin' | 'user' | 'moderator';
  status?: 'active' | 'inactive' | 'suspended' | 'pending';
  preferences?: any;
}

export interface ApiKey {
  id: string;
  userId: string;
  key: string;
  name: string;
  description?: string;
  permissions: string[];
  dataScope?: ApiKeyDataScope;
  isActive: boolean;
  lastUsed?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApiKeyRequest {
  name: string;
  description?: string;
  permissions: string[];
  dataScope?: ApiKeyDataScope;
  expiresAt?: string;
}

class UserManagementApi {
  private async makeRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<any> {
    const response = await makeApiCall(url, {
      ...options,
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // User Management
  async createUser(userData: CreateUserRequest): Promise<User> {
    const result = await this.makeRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    const result = await this.makeRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.makeRequest(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getUser(userId: string): Promise<User> {
    const result = await this.makeRequest(`/admin/users/${userId}`);
    return result.data;
  }

  async listUsers(params: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
  } = {}): Promise<UserListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.role) searchParams.append('role', params.role);
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);

    const result = await this.makeRequest(`/admin/users?${searchParams}`);
    return result.data;
  }

  async getUserStats(): Promise<UserStats> {
    const result = await this.makeRequest('/admin/users/stats');
    return result.data;
  }

  // Current User Management
  async getCurrentUserProfile(): Promise<User> {
    const result = await this.makeRequest('/user/profile');
    return result.data;
  }

  async updateCurrentUserProfile(userData: UpdateUserRequest): Promise<User> {
    const result = await this.makeRequest('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return result.data;
  }

  async getCurrentUserNotifications(): Promise<any[]> {
    const result = await this.makeRequest('/user/notifications');
    return result.data;
  }

  // API Key Management
  async createApiKey(userId: string, apiKeyData: CreateApiKeyRequest): Promise<ApiKey> {
    const result = await this.makeRequest(`/admin/users/${userId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(apiKeyData),
    });
    return result.data;
  }

  async getUserApiKeys(userId: string): Promise<ApiKey[]> {
    const result = await this.makeRequest(`/admin/users/${userId}/api-keys`);
    return result.data;
  }

  async updateApiKey(
    apiKeyId: string,
    updates: { name?: string; description?: string; permissions?: string[]; isActive?: boolean; dataScope?: ApiKeyDataScope | null }
  ): Promise<ApiKey> {
    const result = await this.makeRequest(`/admin/api-keys/${apiKeyId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return result.data;
  }

  async deleteApiKey(apiKeyId: string): Promise<void> {
    await this.makeRequest(`/admin/api-keys/${apiKeyId}`, {
      method: 'DELETE',
    });
  }

  async createCurrentUserApiKey(apiKeyData: CreateApiKeyRequest): Promise<ApiKey> {
    const result = await this.makeRequest('/user/api-keys', {
      method: 'POST',
      body: JSON.stringify(apiKeyData),
    });
    return result.data;
  }

  async getCurrentUserApiKeys(): Promise<ApiKey[]> {
    const result = await this.makeRequest('/user/api-keys');
    return result.data;
  }

  // Notification Management
  async markNotificationAsRead(notificationId: string): Promise<void> {
    await this.makeRequest(`/admin/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  async getUserNotifications(userId: string): Promise<any[]> {
    const result = await this.makeRequest(`/admin/users/${userId}/notifications`);
    return result.data;
  }

  // Utility methods
  async exportUsers(format: 'csv' | 'json' = 'json'): Promise<Blob> {
    const response = await makeApiCall('/admin/users/export', {
      headers: {
        ...(await getAuthHeaders()),
        'Accept': format === 'csv' ? 'text/csv' : 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to export users: ${response.statusText}`);
    }

    return response.blob();
  }

  async importUsers(file: File): Promise<{ success: number; failed: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await makeApiCall('/admin/users/import', {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `Failed to import users: ${response.statusText}`);
    }

    return response.json();
  }

  // Bulk operations
  async bulkUpdateUsers(userIds: string[], updates: UpdateUserRequest): Promise<void> {
    await this.makeRequest('/admin/users/bulk-update', {
      method: 'PUT',
      body: JSON.stringify({ userIds, updates }),
    });
  }

  async bulkDeleteUsers(userIds: string[]): Promise<void> {
    await this.makeRequest('/admin/users/bulk-delete', {
      method: 'DELETE',
      body: JSON.stringify({ userIds }),
    });
  }

  // User activity tracking
  async getUserActivityLogs(userId: string, params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const result = await this.makeRequest(`/admin/users/${userId}/activity?${searchParams}`);
    return result.data;
  }
}

export const userManagementApi = new UserManagementApi();
